import React, {
  useCallback,
  useEffect,
  useState,
} from 'react'
import {
  Button,
  Dialog,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material'

import { lang } from '@common-sense-privacy/common'

import { useCurrentOrganization } from '@/hooks/useSession'
import { usePostEvaluationMutation } from '@/services/organization/product/evaluation'

import { Props } from './types'

export const FAILURE_DIALOG_TITLE = 'We could not complete the automated policy analysis'

export const FAILURE_DIALOG_REASSURANCE = 'Nothing is wrong with your account, and none of your answers were affected.'

export const FAILURE_DIALOG_GUIDANCE = 'You can carry on through the wizard as normal. The automated analysis only ever suggests answers for you to review, so skipping it costs you nothing.'

export const RETRY_LABEL = 'Retry'

export const CLOSE_LABEL = 'Close'

/**
 * Explains a failed evaluation and offers a retry. A failure here is deliberately NOT
 * treated as an account problem - the analysis is an optional assist on top of the
 * wizard - so the copy leads with the reassurance and the retry is opt-in.
 */
function FailureDialog({
  onClose,
  onRetried,
  open,
  productId,
}: Props): React.ReactElement {
  const { id } = useCurrentOrganization()
  const [
    postEvaluation,
    { isLoading: isRetrying },
  ] = usePostEvaluationMutation()

  const [
    retryError,
    setRetryError,
  ] = useState<string>()

  // A stale failure message from a previous visit would otherwise greet the user the
  // next time the dialog opens, before they have retried anything.
  useEffect(() => {
    if (open) setRetryError(undefined)
  }, [ open ])

  const handleRetry = useCallback(() => {
    setRetryError(undefined)

    postEvaluation({
      params: {
        id,
        productId,
      },
    })
      .unwrap()
      .then(() => onRetried())
      // A 409 (an analysis is already running) or a dropped connection leaves the dialog
      // open with the reason spelled out; `isRetrying` going false re-enables the button
      // on its own, so the user is never stuck on a dead dialog.
      .catch(error => setRetryError(error?.data?.message || lang().messages.somethingWentWrong()))
  }, [
    id,
    onRetried,
    postEvaluation,
    productId,
  ])

  return (
    <Dialog open={open} onClose={onClose} data-testid='policy-evaluation-failure-dialog'>
      <DialogContent dividers={true}>
        <Typography variant='h5'>{FAILURE_DIALOG_TITLE}</Typography>
        <Typography mt={2}>{FAILURE_DIALOG_REASSURANCE}</Typography>
        <Typography mt={2}>{FAILURE_DIALOG_GUIDANCE}</Typography>
        {!!retryError && (
          <Typography mt={2} color='error' role='alert'>{retryError}</Typography>
        )}
        <Stack spacing={2} direction='row' justifyContent='center' mt={4}>
          <Button variant='cancel' onClick={onClose} sx={{ width: 'auto' }}>{CLOSE_LABEL}</Button>
          <Button variant='contained' onClick={handleRetry} disabled={isRetrying}>{RETRY_LABEL}</Button>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

export default React.memo(FailureDialog)
